import { useMemo } from "react";
import { ROW_LAYOUTS, itemSchemaOf, createSchemaScope, ancestors, type FieldSchema, type Node, type NodeEntry } from "@broadpaper/core";
import { useSession, useFeatures, nodeLabel } from "../state/context.js";
import type { EditorActions } from "../state/actions.js";
import { FieldRenderer, getPath, type FieldContext } from "./fields/FieldRenderer.js";
import { StyleGroups } from "./StyleGroups.js";
import { ConditionBuilder } from "./fields/ConditionBuilder.js";
import { Section, Field, TextInput, Switch, Button, Badge, Segmented } from "../ui/primitives.js";
import { Icon } from "../ui/icons.js";

/** Scope variables visible to a node: repeater/table aliases from its ancestors. */
export function scopeVarsFor(entry: NodeEntry, session: ReturnType<typeof useSession>): Record<string, FieldSchema> | undefined {
  const chain = ancestors(session.store.index(), entry.node.id);
  const root = createSchemaScope(session.dataSources);
  let vars: Record<string, FieldSchema> = {};
  let scope = root;
  for (const e of chain) {
    const def = session.registry.get(e.node.type);
    const repeat = def?.container?.repeat;
    const isSelf = e.node.id === entry.node.id;
    // Table/chart aliases apply to the node itself; repeater aliases apply to descendants.
    const bindingsWithAlias = session.registry.bindingsOf(e.node.type).filter((b) => b.kind === "collection");
    for (const b of bindingsWithAlias) {
      const appliesHere = repeat ? !isSelf : isSelf;
      if (!appliesHere) continue;
      const props = { ...(typeof def!.defaultProps === "function" ? def!.defaultProps() : def!.defaultProps), ...e.node.props } as Record<string, unknown>;
      const expr = props[b.prop];
      const alias = (b.alias && (props[b.alias] as string)) || "item";
      const item = typeof expr === "string" && expr.trim() ? itemSchemaOf(expr, scope) : undefined;
      const itemSchema = item ?? { type: "object" as const, label: alias, fields: {} };
      vars = { ...vars, [alias]: { ...itemSchema, label: alias }, index: { type: "number", label: "Index" }, first: { type: "boolean", label: "Is first" }, last: { type: "boolean", label: "Is last" }, count: { type: "number", label: "Count" } };
      if (!repeat) vars = { ...vars, row: { ...itemSchema, label: "row" } };
      scope = scope.child(vars);
    }
  }
  return Object.keys(vars).length ? vars : undefined;
}

export function NodeInspector({ entry, actions }: { entry: NodeEntry; actions: EditorActions }) {
  const session = useSession();
  const features = useFeatures();
  const node = entry.node;
  const def = session.registry.get(node.type);
  const theme = session.activeTheme;
  const textStyles = Object.keys(session.store.template.styles.text);
  const scopeVars = useMemo(() => scopeVarsFor(entry, session), [entry, session]);
  const props = useMemo(() => ({ ...(def ? (typeof def.defaultProps === "function" ? def.defaultProps() : def.defaultProps) : {}), ...node.props }) as Record<string, unknown>, [def, node.props]);

  const setProp = (path: string, value: unknown, label = "Edit properties") => session.store.dispatch({ type: "setProp", id: node.id, path, value }, { label, mergeKey: `prop:${node.id}:${path}` });
  const ctx: FieldContext = {
    dataSources: session.dataSources,
    scopeVars,
    theme,
    textStyles,
    node,
    props,
    setProps: (patch) => session.store.dispatch({ type: "setProps", id: node.id, patch }),
    allowExpressions: features.expressions
  };

  const label = nodeLabel(session, node);
  const issues = session.ui.get().issues.filter((i) => i.nodeId === node.id);

  return (
    <>
      <div className="bpe-inspector__header">
        <span className="bpe-inspector__icon"><Icon name={def?.icon ?? "box"} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="bpe-inspector__title" title={label}>{label}</div>
          <div className="bpe-inspector__subtitle">{def?.name ?? node.type}{def?.license && def.license !== "community" ? ` · ${def.license}` : ""}</div>
        </div>
        <div className="bpe-inspector__actions">
          <Button size="sm" variant="ghost" icon={node.locked ? "lock" : "unlock"} tooltip={node.locked ? "Unlock" : "Lock"} onClick={() => actions.toggleLock()} />
          {node.type !== "column" ? <Button size="sm" variant="ghost" icon="trash" tooltip="Delete" onClick={() => actions.deleteSelection()} disabled={!!node.locked} /> : null}
        </div>
      </div>
      <div className="bpe-inspector__body">
        {issues.length ? (
          <div style={{ padding: "8px 12px 0" }}>
            {issues.map((i, k) => (
              <div key={k} className={`bpe-issue bpe-issue--${i.severity}`} style={{ padding: "4px 0" }}>
                <Icon name={i.severity === "error" ? "alert-circle" : i.severity === "warning" ? "alert-triangle" : "info"} />
                <span>{i.message}</span>
              </div>
            ))}
          </div>
        ) : null}
        {!def ? <div className="bpe-empty"><Icon name="alert-triangle" /><div className="bpe-empty__title">Unknown block “{node.type}”</div>This block is not registered in this editor. Its settings are preserved.</div> : null}

        {node.type === "row" ? (
          <Section title="Layout">
            <div className="bpe-prop-grid">
              <Field label="Columns" stacked>
                <div className="bpe-layouts__grid" style={{ width: "100%" }}>
                  {ROW_LAYOUTS.map((l) => (
                    <button key={l.id} type="button" className="bpe-layout-item" aria-pressed={JSON.stringify(l.layout) === JSON.stringify(node.props.layout)} style={JSON.stringify(l.layout) === JSON.stringify(node.props.layout) ? { borderColor: "var(--bpe-accent)", background: "var(--bpe-accent-soft)" } : undefined} onClick={() => session.store.dispatch({ type: "setRowLayout", id: node.id, layout: l.layout })}>
                      <div className="bpe-layout-item__cols" style={{ gridTemplateColumns: l.layout.map((c) => `${c}fr`).join(" ") }}>
                        {l.layout.map((_, i) => <div key={i} className="bpe-layout-item__col" />)}
                      </div>
                      <span className="bpe-layout-item__name">{l.label}</span>
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          </Section>
        ) : null}

        {node.type === "column" ? (
          <Section title="Column">
            <div className="bpe-prop-grid">
              <Field label="Width" hint="Twelfths of the row. Change the row layout to resize columns.">
                <Badge>{String(node.props.span)} / 12</Badge>
              </Field>
              <Field label="Row"><Button size="sm" onClick={() => actions.selectParent()}>Select row</Button></Field>
            </div>
          </Section>
        ) : null}

        {(def?.inspector ?? []).map((section, si) => {
          if (section.when && !section.when(props)) return null;
          const fields = section.fields.filter((f) => !f.when || f.when(props));
          if (!fields.length) return null;
          return (
            <Section key={si} title={section.title} defaultOpen={!section.collapsed}>
              <div className="bpe-prop-grid">
                {fields.map((f) => (
                  <FieldRenderer key={f.prop} field={f} value={getPath(props, f.prop)} onChange={(v) => setProp(f.prop, v)} ctx={ctx} />
                ))}
              </div>
            </Section>
          );
        })}

        {def ? (
          <StyleGroups
            node={node}
            groups={def.styleGroups ?? ["spacing", "flow"]}
            theme={theme}
            textStyles={textStyles}
            allowOverrides={features.styleOverrides}
            setStyle={(patch) => session.store.dispatch({ type: "setStyle", id: node.id, patch }, { label: "Edit style", mergeKey: `style:${node.id}` })}
            setFlow={(patch) => session.store.dispatch({ type: "setFlow", id: node.id, patch }, { label: "Edit page flow" })}
          />
        ) : null}

        {node.type !== "column" ? (
          <Section title="Visibility" defaultOpen={!!node.visibleWhen} icon="eye">
            <div className="bpe-prop-grid">
              <Field label="Show when" stacked hint="Leave empty to always show this block.">
                <ConditionBuilder value={node.visibleWhen} onChange={(v) => session.store.dispatch({ type: "setVisibleWhen", id: node.id, condition: v }, { label: "Edit visibility" })} dataSources={session.dataSources} scopeVars={scopeVars} allowExpression={features.expressions} />
              </Field>
            </div>
          </Section>
        ) : null}

        <Section title="Advanced" defaultOpen={false} icon="settings">
          <div className="bpe-prop-grid">
            <Field label="Name" hint="Shown in the layers panel and breadcrumb.">
              <TextInput value={node.name ?? ""} onChange={(v) => session.store.dispatch({ type: "setName", id: node.id, name: v || undefined })} lazy placeholder={def?.name} />
            </Field>
            <Field label="Locked"><Switch checked={!!node.locked} onChange={(v) => session.store.dispatch({ type: "setLocked", id: node.id, locked: v })} /></Field>
            <Field label="ID"><code className="bpe-mono bpe-muted" style={{ fontSize: 10 }}>{node.id}</code></Field>
            <Field label="Type"><code className="bpe-mono bpe-muted" style={{ fontSize: 10 }}>{node.type}</code></Field>
            {node.type !== "section" && node.type !== "column" ? (
              <Field label="Wrap in">
                <Segmented value="" onChange={(v) => actions.wrapSelectionIn(v as never)} options={[{ value: "card", label: "Card" }, { value: "keepTogether", label: "Keep" }, { value: "conditional", label: "If" }]} />
              </Field>
            ) : null}
          </div>
        </Section>
      </div>
    </>
  );
}

export type { Node };
